import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import { setTriggerMarketInfoSearch, setSearchResultTotalRecord, resetMarketInfoSearch, setSearchType, MarketInfoSearchSelector, setMarketInfoSearchResults, setVesselNames, setTriggerFocusVesselName, setOwners, setOwnerContactNames, setChartererContactNames, setOwnerContactSearchKeyword, setChartererContactSearchKeyword, setCharterers, setMarketInfoType, setCargoTypes, setLoadPortAreas, setDisportAreas, setSizeMin, setSizeMax, setUpdatedDateFrom, setTimerId, setFirstSearchAlready } from './reducers/MarketInfoSearchReducer';
import MarketInfoApi, { SearchMarketInfoParam } from './api/MarketInfoApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch } from '../SavedSearch/reducers/SavedSearchReducer';
import MarketInfoModel from './models/MarketInfoModel';
import TagsFilter from '../TagFilter/TagsFilter';
import { searchContactName } from '../ContactSelector/ContactSelector';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import AreaModel from '../PortSelector/models/AreaModel';
import { SearchType } from '../Tonnage/reducers/TonnageSearchReducer';
import { tonnageInputSelector } from '../Tonnage/reducers/TonnageInputReducer';
import { MarketInfoType } from './models/MarketInfoQueryModel';
import CargoTypeMultipleSelector from '../CargoTypeSelector/CargoTypeMultipleSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import { cargoTypesToQueryItems, queryItemsToCargoTypes } from '../Cargo/CargoSearch';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';

const MarketInfoSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const marketInfoSearchData = useSelector(MarketInfoSearchSelector);
    const tonnageViewData = useSelector(tonnageInputSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);
    
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (marketInfoSearchData.ownerContactSearchKeyword && marketInfoSearchData.ownerContactSearchKeyword.length > 0) {
                searchContactName(marketInfoSearchData.ownerContactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setOwnerContactNames(contacts));
                    } else {
                        dispatch(setOwnerContactNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [marketInfoSearchData.ownerContactSearchKeyword])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (marketInfoSearchData.chartererContactSearchKeyword && marketInfoSearchData.chartererContactSearchKeyword.length > 0) {
                searchContactName(marketInfoSearchData.chartererContactSearchKeyword, (contacts) => {
                    if (contacts && contacts.length > 0) {
                        dispatch(setChartererContactNames(contacts));
                    } else {
                        dispatch(setChartererContactNames([]));
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [marketInfoSearchData.chartererContactSearchKeyword])

    useEffect(() => {
        if (!marketInfoSearchData.firstSearchAlready) {
            runUpdateData();
            let timerId = setInterval(() => {
                runUpdateData();
            }, 60 * 1000);

            dispatch(setTimerId(timerId));
        }
    }, []);

    useEffect(() => {
        if (marketInfoSearchData.firstSearchAlready && marketInfoSearchData.timerId) {
            clearInterval(marketInfoSearchData.timerId);
        }
    }, [marketInfoSearchData.firstSearchAlready]);

    const runUpdateData = () => {
        MarketInfoApi.GetUpdatedData()
            .then((response: BaseResponse<MarketInfoModel[]>) => {
                if (response.success) {
                    dispatch(setMarketInfoSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                    dispatch(setSearchType(SearchType.Search));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
            });
    }

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.MarketInfo,
                    queryJson: JSON.stringify(marketInfoSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (marketInfoSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        MarketInfoApi.Search({
            query: marketInfoSearchData.query
        } as SearchMarketInfoParam)
            .then((response: BaseResponse<MarketInfoModel[]>) => {
                if (response.success) {
                    dispatch(setMarketInfoSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                    dispatch(setSearchType(SearchType.Search));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [marketInfoSearchData.triggerSearch])

    const areasToQueryItems = (areas: AreaModel[]) => {
        return _.map(areas, (area) => {
            return {
                id: area.name,
                value: area.name,
                positive: area.positive
            } as QueryItem
        })
    }

    const queryItemsToAeras = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                name: queryItem.value,
                positive: queryItem.positive
            } as AreaModel
        })
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.MarketInfo}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerMarketInfoSearch((new Date()).getTime()));
                                                dispatch(setFirstSearchAlready(true));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetMarketInfoSearch());
                                                dispatch(setTriggerFocusVesselName((new Date()).getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(tonnageViewData.tonnageNames, (item) => {
                            return {
                                id: item.id,
                                value: item.vesselName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Vessel Name"
                        className="tonnage-search-item"
                        value={marketInfoSearchData.query.vesselNames}
                        useFreeText={true}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setVesselNames(value))
                        }}
                        triggerFocusInput={marketInfoSearchData.triggerFocusVesselName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(marketInfoSearchData.ownerContactNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Owners Name"
                        useFreeText={true}
                        onInputChange={(text: string) => {
                            dispatch(setOwnerContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={marketInfoSearchData.query.owners}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setOwners(value))
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(marketInfoSearchData.chartererContactNames, (item) => {
                            return {
                                id: item.id,
                                value: item.companyName,
                                positive: true
                            } as QueryItem
                        })}
                        label="Charterers Name"
                        useFreeText={true}
                        onInputChange={(text: string) => {
                            dispatch(setChartererContactSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={marketInfoSearchData.query.charterers}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCharterers(value))
                        }}
                    ></TagsFilter>
                    <Autocomplete
                        className='tonnage-search-item'
                        disableClearable={true}
                        options={[MarketInfoType.All, MarketInfoType.Voy, MarketInfoType.TCT, MarketInfoType.Period]}
                        renderInput={(params) => <TextField {...params} label="Biz" />}
                        value={marketInfoSearchData.query.marketInfoType}
                        onChange={(event: React.SyntheticEvent, value: MarketInfoType, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                            dispatch(setMarketInfoType(value));
                        }}
                    />
                    <TagsFilter
                        data={_.map(cargoTypeSelectorData.cargoTypes, (item) => {
                            return {
                                id: item.name,
                                value: item.name,
                                positive: true
                            } as QueryItem
                        })}
                        label="Cargo"
                        useFreeText={true}
                        className="tonnage-search-item"
                        value={marketInfoSearchData.query.cargoTypes}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCargoTypes(value))
                        }}
                    ></TagsFilter>
                    <PortMultipleSelector
                        onAreaChange={(area: AreaModel[]) => {
                            if (area) {
                                dispatch(setLoadPortAreas(areasToQueryItems(area)));
                            }
                        }}
                        useFreeText={true}
                        areaLabel='Load/Delivery Area'
                        areaValues={queryItemsToAeras(marketInfoSearchData.query.loadPortAreas)}
                        className="tonnage-search-item"
                        onlyAreaMode={true}
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <PortMultipleSelector
                        onAreaChange={(area: AreaModel[]) => {
                            if (area) {
                                dispatch(setDisportAreas(areasToQueryItems(area)));
                            }
                        }}
                        useFreeText={true}
                        areaLabel='Dis/Redel Area'
                        areaValues={queryItemsToAeras(marketInfoSearchData.query.disportAreas)}
                        className="tonnage-search-item"
                        onlyAreaMode={true}
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <Grid container>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Size min"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={marketInfoSearchData.query.sizeMin ? marketInfoSearchData.query.sizeMin : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setSizeMin(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                className='tonnage-search-item'
                                fullWidth
                                label="Size max"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={marketInfoSearchData.query.sizeMax ? marketInfoSearchData.query.sizeMax : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setSizeMax(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Autocomplete
                            fullWidth
                            className='tonnage-search-item'
                            options={["1", "3", "7", "15", "21", "30", "45", "60", "90"]}
                            renderInput={(params) => <TextField {...params} label="Updated Date From" />}
                            value={marketInfoSearchData.query.updatedDateFrom.toString()}
                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setUpdatedDateFrom(value ? parseInt(value) : 0));
                            }}
                        />
                    </Grid>
                </div>
            </CustomAccordion>
        </>
    )
};

export default MarketInfoSearch;